import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown, Menu } from 'antd';
import { Tags as TagsIcon } from 'lucide-react';
import { MdAddChart } from 'react-icons/md';
import { TbCloudBolt, TbCopy, TbCrosshair, TbDots } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import AlertEditorModal from '@/layouts/02-Authenticated/layout/03-Content/routes/12-Settings/layout/02-Content/routes/Account/03-Notifications/components/AlertEditorModal';
import { useAlertsCatalog } from '@/queries';
import {
    CreateAlertIcon,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    PlanTag,
    SmallTooltip,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useInvestmentStore } from '@/stores';
import { nanoId } from '@/utilities';

import type { CompanyActionResult } from '../../../01-ActionsRow';
import BulkCompanyAddModal from '../../../01-ActionsRow/BulkCompanyAddModal';
import SyncToDataSourcesModal from '../../../01-ActionsRow/SyncToConnectedAccountModal';

interface CompanyRowActionsProps {
    detailRoute?: string;
    onCreateInvestment: () => Promise<void>;
    result: CompanyActionResult;
    selectionId?: string;
}

const CompanyRowActions: FC<CompanyRowActionsProps> = ({
    detailRoute,
    onCreateInvestment,
    result,
    selectionId,
}): JSX.Element => {
    const [addModalKind, setAddModalKind] = useState<'tag' | 'watchlist'>();
    const [createAlertOpen, setCreateAlertOpen] = useState(false);
    const [syncOpen, setSyncOpen] = useState(false);
    const [workflowSelection, setWorkflowSelection] = useState<Set<string>>(new Set());
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const { investments } = useInvestmentStore();
    const createInvestmentAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);
    const canUseAlerts = hasFeature('alerts');
    const alertsCatalog = useAlertsCatalog(canUseAlerts);
    const enrichment = (result.enrichment ?? {}) as Record<string, unknown>;
    const symbol = (enrichment.symbol as string | undefined) ?? result.symbol;
    const resultId = selectionId ?? result.entityId ?? symbol;
    const companyName =
        (enrichment.companyName as string | undefined) ??
        (result.companyName as string | undefined) ??
        (result.name as string | undefined) ??
        symbol ??
        'Company';
    const isConnectedCompany = result.resultType === 'nylasCompany';
    const isImportedEntity = result.resultType === 'importedEntity';
    const canUsePublicCompanyActions = Boolean(symbol && !isConnectedCompany && !isImportedEntity);
    const selectedResultSet = useMemo(() => (resultId ? new Set([String(resultId)]) : new Set<string>()), [resultId]);

    const openSync = () => {
        setWorkflowSelection(selectedResultSet);
        setSyncOpen(true);
    };

    const copyLink = async () => {
        if (!detailRoute) {
            return;
        }

        try {
            await navigator.clipboard.writeText(`${window.location.origin}${detailRoute}`);
            toast.success('Company link copied.');
        } catch (error) {
            captureException(error);
            toast.error('Unable to copy this company link.');
        }
    };

    return (
        <>
            <Dropdown
                placement='bottomRight'
                trigger={['click']}
                popupRender={() => (
                    <DropdownPanel
                        header={<DropdownMenuLabel icon={<TbDots size='0.875rem' />}>Actions</DropdownMenuLabel>}
                        width={21 * 16}
                    >
                        <DropdownPanelMenu selectable={false}>
                            <Menu.Item
                                disabled={createInvestmentAccess.disabled || !canUsePublicCompanyActions}
                                key='create-investment'
                                onClick={() => {
                                    if (!createInvestmentAccess.disabled) {
                                        void onCreateInvestment();
                                    }
                                }}
                            >
                                <DropdownOptionContent
                                    description='Create a workspace investment for this company.'
                                    icon={<MdAddChart size='1rem' />}
                                    title='Create Investment'
                                />
                            </Menu.Item>
                            <Menu.Item
                                disabled={!canUseAlerts || !alertsCatalog.data || !canUsePublicCompanyActions}
                                key='create-alert'
                                onClick={() => {
                                    if (canUseAlerts) {
                                        setCreateAlertOpen(true);
                                    }
                                }}
                            >
                                <DropdownOptionContent
                                    description='Monitor material events and changes for this company.'
                                    icon={<CreateAlertIcon />}
                                    title={
                                        <span style={{ alignItems: 'center', display: 'inline-flex' }}>
                                            Create Alert
                                            <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
                                        </span>
                                    }
                                />
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item disabled={!symbol || isConnectedCompany} key='sync' onClick={openSync}>
                                <DropdownOptionContent
                                    description='Send this company to a connected data source.'
                                    icon={<TbCloudBolt size='1rem' />}
                                    title='Sync to Data Sources'
                                />
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item key='add-tag' onClick={() => setAddModalKind('tag')}>
                                <DropdownOptionContent
                                    description='Attach an existing classification to this company.'
                                    icon={<TagsIcon size='1rem' />}
                                    title='Add Tag'
                                />
                            </Menu.Item>
                            <Menu.Item key='add-watchlist' onClick={() => setAddModalKind('watchlist')}>
                                <DropdownOptionContent
                                    description='Add this company to an existing watchlist.'
                                    icon={<TbCrosshair size='1rem' />}
                                    title='Add to Watchlist'
                                />
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item disabled={!detailRoute} key='copy-link' onClick={() => void copyLink()}>
                                <DropdownOptionContent
                                    description='Copy a direct link to this company.'
                                    icon={<TbCopy size='1rem' />}
                                    title='Copy Link'
                                />
                            </Menu.Item>
                        </DropdownPanelMenu>
                    </DropdownPanel>
                )}
            >
                <div>
                    <SmallTooltip title='Company actions'>
                        <Button
                            aria-label={`Actions for ${companyName}`}
                            type='text'
                            icon={
                                <TbDots
                                    size='1rem'
                                    style={{ color: token.colorTextTertiary, verticalAlign: '-0.175rem' }}
                                />
                            }
                        />
                    </SmallTooltip>
                </div>
            </Dropdown>
            <SyncToDataSourcesModal
                open={syncOpen}
                results={[result]}
                resultsSelected={workflowSelection}
                setOpen={setSyncOpen}
                setResultsSelected={setWorkflowSelection}
            />
            {addModalKind ? (
                <BulkCompanyAddModal
                    kind={addModalKind}
                    open
                    results={[result]}
                    onClose={() => setAddModalKind(undefined)}
                />
            ) : null}
            {canUseAlerts && createAlertOpen && alertsCatalog.data && symbol ? (
                <AlertEditorModal
                    catalog={alertsCatalog.data}
                    initialInput={{
                        category: 'companies',
                        deliveryMethods: ['inApp'],
                        description: `Monitor material events and changes for ${companyName}.`,
                        enabled: true,
                        name: `${companyName} Alert`,
                        query: {
                            conditions: [
                                {
                                    field: 'symbol',
                                    id: nanoId(12, true),
                                    operator: 'equals',
                                    value: symbol,
                                },
                            ],
                            match: 'all',
                            version: 1,
                        },
                    }}
                    initialView='create'
                    open
                    onClose={() => setCreateAlertOpen(false)}
                />
            ) : null}
        </>
    );
};

export default CompanyRowActions;
